
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { ClubManagement } from '@/components/admin/ClubManagement';
import { TeamManagement } from '@/components/admin/TeamManagement';
import { ProgramManagement } from '@/components/admin/ProgramManagement';
import { OrganizationUserManagement } from '@/components/admin/OrganizationUserManagement';
import { SeasonManagement } from '@/components/admin/SeasonManagement';
import { DivisionManagement } from '@/components/admin/DivisionManagement';
import { PlayerManagement } from '@/components/admin/PlayerManagement';
import { useOrganizationStats } from '@/hooks/useOrganizationStats';
import { Building2, Users, Trophy, Calendar, Target, UserCheck, BookOpen } from 'lucide-react';

const Admin = () => {
  const { profile, loading, currentOrganization } = useAuth();
  const { data: stats, isLoading: statsLoading } = useOrganizationStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile || profile.role !== 'member') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Administration</h1>
          <p className="text-muted-foreground mt-2">
            Manage your organization, clubs, teams, and members
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Clubs</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.totalClubs || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Teams</CardTitle>
              <Trophy className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.totalTeams || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Players</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.totalPlayers || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Seasons</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.activeSeasons || 0}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="clubs" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="clubs" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Clubs
            </TabsTrigger>
            <TabsTrigger value="teams" className="flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              Teams
            </TabsTrigger>
            <TabsTrigger value="programs" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Programs
            </TabsTrigger>
            <TabsTrigger value="seasons" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Seasons
            </TabsTrigger>
            <TabsTrigger value="divisions" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Divisions
            </TabsTrigger>
            <TabsTrigger value="players" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Players
            </TabsTrigger>
            <TabsTrigger value="members" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Members
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clubs">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Club Management
                </CardTitle>
                <CardDescription>
                  Create and manage clubs within your organization
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ClubManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="teams">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  Team Management
                </CardTitle>
                <CardDescription>
                  Create and manage teams across your clubs and seasons
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TeamManagement organizationId={currentOrganization?.id} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="programs">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Program Management
                </CardTitle>
                <CardDescription>
                  Create and manage training programs for your teams
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ProgramManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="seasons">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Season Management
                </CardTitle>
                <CardDescription>
                  Create and manage seasons for your clubs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SeasonManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="divisions">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Division Management
                </CardTitle>
                <CardDescription>
                  Create and manage divisions within your seasons
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DivisionManagement seasonId="" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="players">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Player Management
                </CardTitle>
                <CardDescription>
                  Manage players across your organization
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PlayerManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5" />
                  Organization Members
                </CardTitle>
                <CardDescription>
                  Manage user roles within your organization
                </CardDescription>
              </CardHeader>
              <CardContent>
                <OrganizationUserManagement />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
